import { Check, X } from "lucide-react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

interface AccessRequest {
  id: number;
  email: string;
  requested_at?: string;
  created_at: string;
  resolved_at?: string;
  solution?: string;
}

interface Props {
  requests: AccessRequest[];
  isLoading: boolean;
  isPending: boolean;
  onResolve: (requestId: number, solution: "approved" | "denied") => void;
}

const AccessRequestsList = ({ requests, isLoading, isPending, onResolve }: Props) => {
  const pendingRequests = requests.filter((r) => !r.resolved_at);
  const resolvedRequests = requests.filter((r) => r.resolved_at);

  if (isLoading) return <Card className="p-8 text-center text-gray-400">Loading...</Card>;

  if (requests.length === 0) {
    return (
      <Card className="p-8 text-center text-gray-400 text-sm">
        No access requests for this genie
      </Card>
    );
  }

  return (
    <Card className="divide-y divide-gray-100">
      {pendingRequests.map((req) => (
        <div key={req.id} className="flex items-center justify-between p-4 gap-4">
          <div className="min-w-0">
            <CopyableEmail email={req.email} className="text-sm font-medium text-gray-800" textClassName="truncate" />
            <p className="text-xs text-gray-400 mt-0.5">
              Requested{" "}
              {req.requested_at
                ? new Date(req.requested_at).toLocaleDateString()
                : new Date(req.created_at).toLocaleDateString()}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              size="sm"
              className="bg-gradient-to-r from-genie-primary to-genie-secondary text-white font-semibold shadow-sm"
              onClick={() => onResolve(req.id, "approved")}
              disabled={isPending}
            >
              <Check className="h-3.5 w-3.5 mr-1" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onResolve(req.id, "denied")}
              disabled={isPending}
            >
              <X className="h-3.5 w-3.5 mr-1" />
              Deny
            </Button>
          </div>
        </div>
      ))}
      {resolvedRequests.map((req) => (
        <div key={req.id} className="flex items-center justify-between p-4 gap-4 opacity-60">
          <div className="min-w-0">
            <CopyableEmail email={req.email} className="text-sm font-medium text-gray-700" textClassName="truncate" />
            <p className="text-xs text-gray-400 mt-0.5">
              {new Date(req.resolved_at!).toLocaleDateString()}
            </p>
          </div>
          <Badge
            className={
              req.solution === "approved"
                ? "bg-genie-primary/10 text-genie-primary"
                : "bg-red-50 text-red-500"
            }
          >
            {req.solution === "approved" ? "Approved" : "Denied"}
          </Badge>
        </div>
      ))}
    </Card>
  );
};

export default AccessRequestsList;
